import { useEffect } from "react"
import { createPortal } from "react-dom"
import { ExclamationTriangleIcon, TrashIcon } from "@heroicons/react/16/solid"

type Props = {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  loading?: boolean
  danger?: boolean
  onConfirm: () => void
  onCancel: () => void
}

export const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = "Удалить",
  cancelLabel = "Отмена",
  loading = false,
  danger = true,
  onConfirm,
  onCancel,
}: Props) => {
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onCancel()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [open, loading, onCancel])

  if (!open) return null

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[80] bg-black/30 backdrop-blur-[2px]"
        onClick={() => { if (!loading) onCancel() }}
      />
      <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center mb-4 ${danger ? "bg-red-50" : "bg-blue-50"}`}>
            <ExclamationTriangleIcon className={`w-5 h-5 ${danger ? "text-red-500" : "text-blue-500"}`} />
          </div>

          <h2 className="text-base font-semibold text-gray-900 mb-1.5">{title}</h2>
          <p className="text-sm text-gray-500 leading-relaxed mb-5">{message}</p>

          <div className="flex gap-2">
            <button
              type="button"
              disabled={loading}
              onClick={onCancel}
              className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-40"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={onConfirm}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors disabled:opacity-40 ${
                danger
                  ? "bg-red-600 hover:bg-red-700 shadow-sm shadow-red-200"
                  : "bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-200"
              }`}
            >
              {loading
                ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <TrashIcon className="w-4 h-4" />
              }
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}